/**
 * Trophy visuals and pure helpers for the UT Trophies screens.
 * Colours, codes and icon paths come from docs/design/ut-v2/wnf-data-trophies.js.
 */
import type { AwardCategory, MedalType } from '../../../types/awards';
import { AWARD_CATEGORIES, AWARD_CATEGORY_ORDER } from '../../../constants/awards';

export type TrophyMedal = MedalType | 'spoon';
/** A season, or 'all' for all-time. */
export type TrophyYear = number | 'all';

export interface TrophyCategoryMeta {
  id: AwardCategory;
  title: string;
  /** Plain-English description (never the scoring formula). */
  desc: string;
  code: string;
  /** Category glow colour and the dark end of the card background. */
  cc: string;
  cd: string;
  /** SVG path (24×24 viewBox, stroked). */
  icon: string;
  kind: 'solo' | 'pair' | 'trio';
  /** Wooden spoon awards: shown on their own shelf, never counted in totals. */
  cursed: boolean;
  /** Ranked by an internal score, so show records / names, never the number. */
  scored: boolean;
}

const ci = (x: number, y: number, r: number) => `M${x - r} ${y}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`;

const ICONS: Record<AwardCategory, string> = {
  xp_champion: 'M3 8l4.5 4.5L12 5l4.5 7.5L21 8l-2 10H5z M5 21h14',
  win_rate_leader: ci(12, 12, 9) + ci(12, 12, 5) + ci(12, 12, 1),
  net_positive: 'M2 20V6h20v14 M12 9v8 M8 13h8',
  iron_man: 'M12 3l8 3v5.5c0 4.8-3.4 8.3-8 9.5-4.6-1.2-8-4.7-8-9.5V6z M8 10h8 M8 14h8',
  hot_streak: 'M12 3c.8 3.2 5 5.2 5 10.2a5 5 0 0 1-10 0c0-2.2 1-3.8 2.2-4.8.1 1.8.9 2.8 1.8 3.1C11 8.8 10.4 6 12 3z',
  the_wall: 'M3 5h18v14H3z M3 9.7h18 M3 14.3h18 M9 5v4.7 M15 5v4.7 M6 9.7v4.6 M12 9.7v4.6 M18 9.7v4.6 M9 14.3V19 M15 14.3V19',
  appearance_king:
    'M8.5 3L3 6l2 4 3-1.2V21h8V8.8l3 1.2 2-4-5.5-3c-.6 1.6-1.9 2.5-3.5 2.5S9.1 4.6 8.5 3z M12 11.5v5.5 M10.8 12.6l1.2-1.1',
  dynamic_duo: ci(9, 12, 5.5) + ci(15, 12, 5.5),
  best_buddies: ci(8, 8, 3) + ci(16, 8, 3) + 'M2 20c0-3.3 2.7-6 6-6s6 2.7 6 6 M10 20c0-3.3 2.7-6 6-6s6 2.7 6 6',
  fiercest_rivalry: 'M4 4l11 11 M13 17l4-4 M15 15l4 4 M20 4L9 15 M7 13l4 4 M9 15l-4 4',
  dream_team_trio:
    ci(12, 5.5, 2.5) + ci(5.5, 17.5, 2.5) + ci(18.5, 17.5, 2.5) + 'M10.8 7.7L6.7 15.3 M13.2 7.7l4.1 7.6 M8 17.5h8',
  blue_blood: 'M12 3c3.6 4.6 6 7.8 6 11.2a6 6 0 0 1-12 0C6 10.8 8.4 7.6 12 3z M9 14.5a3 3 0 0 0 3 3',
  dutch_master: 'M10 21l.8-9h2.4l.8 9 M12 9L6 3 M12 9l6-6 M12 9l6 6 M12 9l-6 6' + ci(12, 9, 1.2),
  super_sub: 'M8 20V5 M4 9l4-4 4 4 M16 4v15 M12 15l4 4 4-4',
  cursed_duos:
    'M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7a4.3 4.3 0 0 1 7.5 2.8C19.5 15.4 12 20 12 20z M12 7l-1.6 3.4 3.2 2-2 3.6',
  cursed_trio: 'M6 21V10a6 6 0 0 1 12 0v11l-2-1.6-2 1.6-2-1.6-2 1.6-2-1.6z' + ci(9.8, 10.5, 0.9) + ci(14.2, 10.5, 0.9),
};

// code, glow, dark, plain description
const LOOK: Record<AwardCategory, [string, string, string, string]> = {
  xp_champion: ['XP', '#f59e0b', '#3a2606', 'Highest XP. All-time: the highest XP anyone has ever reached'],
  win_rate_leader: ['WR', '#10b981', '#06301f', 'Best win rate (min 10 even-team games)'],
  net_positive: ['+/−', '#f97316', '#3a1a06', 'Best team goal difference'],
  iron_man: ['IM', '#6366f1', '#171a45', 'Longest attendance streak'],
  hot_streak: ['HS', '#f43f5e', '#3f0a16', 'Longest win streak'],
  the_wall: ['TW', '#818cf8', '#1b1d4a', 'Longest unbeaten streak'],
  appearance_king: ['AK', '#9333ea', '#2a0c45', 'Most games played'],
  dynamic_duo: ['DD', '#ec4899', '#3f0b26', 'Best record as a pair on the same team'],
  best_buddies: ['BB', '#14b8a6', '#06302b', 'Most games played together'],
  fiercest_rivalry: ['FR', '#dc2626', '#3a0808', 'Most one-sided head-to-head'],
  dream_team_trio: ['DT', '#a855f7', '#2c0d45', 'Best record as a three on the same team'],
  blue_blood: ['BL', '#3b82f6', '#0b1f45', 'Highest % of games on Blue'],
  dutch_master: ['DM', '#fb923c', '#3a1d08', 'Highest % of games on Orange'],
  super_sub: ['SS', '#2dd4bf', '#06302b', 'Most reserve appearances'],
  cursed_duos: ['CD', '#9f1239', '#2a0710', 'Worst record as a pair on the same team'],
  cursed_trio: ['CT', '#475569', '#141a22', 'Worst record as a three on the same team'],
};

const CURSED = new Set<AwardCategory>(['cursed_duos', 'cursed_trio']);
const SCORED = new Set<AwardCategory>(['dynamic_duo', 'fiercest_rivalry', 'dream_team_trio', 'cursed_duos', 'cursed_trio']);

export const TROPHY_META: Record<AwardCategory, TrophyCategoryMeta> = Object.fromEntries(
  AWARD_CATEGORY_ORDER.map((id) => {
    const cfg = AWARD_CATEGORIES[id];
    const [code, cc, cd, desc] = LOOK[id];
    const kind = cfg.isTrioAward ? 'trio' : cfg.isPairAward ? 'pair' : 'solo';
    const title = id === 'cursed_duos' ? 'Cursed Duo' : cfg.title;
    return [id, { id, title, desc, code, cc, cd, icon: ICONS[id], kind, cursed: CURSED.has(id), scored: SCORED.has(id) }];
  }),
) as Record<AwardCategory, TrophyCategoryMeta>;

/** Honours: fixed distinctions on their own shelf, not ranked Hall of Fame awards. */
export type HonourId = 'og';
/** Anything drawn as a trophy: an award category or an honour. */
export type TrophyKind = AwardCategory | HonourId;

type TrophyLook = Pick<TrophyCategoryMeta, 'title' | 'desc' | 'code' | 'cc' | 'cd' | 'icon'>;

/** OG: played in WNF's first 10 games (gold 8-10, silver 5-7, bronze 1-4). See player_og_status. */
export const OG_META: TrophyLook = {
  title: 'OG',
  desc: "Played in WNF's first 10 games (May–Aug 2024)",
  code: 'OG',
  cc: '#38bdf8',
  cd: '#082f49',
  // A star: one of the originals
  icon: 'M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.6 6.6 19.5l1.2-6L3.3 9.3l6.1-.7z',
};

/** Title, colours and icon for an award or an honour. */
export const trophyLook = (c: TrophyKind): TrophyLook => (c === 'og' ? OG_META : TROPHY_META[c]);

/** Display order. Main awards first; the two cursed awards are the wooden spoon shelf. */
export const MAIN_CATEGORIES = AWARD_CATEGORY_ORDER.filter((c) => !CURSED.has(c));
export const SPOON_CATEGORIES = AWARD_CATEGORY_ORDER.filter((c) => CURSED.has(c));

export const MEDAL_STYLE: Record<TrophyMedal, { from: string; to: string; label: string; name: string; ink: string; text: string }> = {
  gold: { from: '#f7de8e', to: '#b0842a', label: 'GOLD', name: 'Gold', ink: '#1a1204', text: '#f7de8e' },
  silver: { from: '#eef1f5', to: '#8e96a3', label: 'SILVER', name: 'Silver', ink: '#1a1204', text: '#eef1f5' },
  bronze: { from: '#f2b58a', to: '#a0552a', label: 'BRONZE', name: 'Bronze', ink: '#1a1204', text: '#f2b58a' },
  spoon: { from: '#b98555', to: '#5a3a22', label: 'SPOON', name: 'Wooden spoon', ink: '#1a0f06', text: '#d7a877' },
};

export const MEDALS: MedalType[] = ['gold', 'silver', 'bronze'];
export const medalAt = (position: number): MedalType => MEDALS[Math.min(Math.max(position, 1), 3) - 1];
export const medalGradient = (m: TrophyMedal) => `linear-gradient(150deg, ${MEDAL_STYLE[m].from}, ${MEDAL_STYLE[m].to})`;

export const yearLabel = (y: TrophyYear) => (y === 'all' ? 'ALL-TIME' : String(y));
export const yearName = (y: TrophyYear) => (y === 'all' ? 'All-time' : String(y));
export const yearKey = (y: TrophyYear) => String(y);
export const parseYear = (s: string | null | undefined): TrophyYear | null => {
  if (s === 'all') return 'all';
  const n = Number(s);
  return Number.isInteger(n) && n > 2000 ? n : null;
};

/** "Chris", "Chris & Dom", "Chris, Dom & Jude" */
export function joinNames(names: string[], joiner = ' & '): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return names.join(joiner);
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${Math.abs(n) === 1 ? one : many}`;
const trim1 = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/**
 * The value printed on a trophy for a solo award ("708 XP", "68.9%", "+20").
 * Score-based awards return null: their number means nothing to players, so
 * callers show a record or the names instead.
 */
export function trophyValue(category: AwardCategory, value: number): string | null {
  if (TROPHY_META[category].scored) return null;
  switch (category) {
    case 'xp_champion':
      return `${Math.round(value).toLocaleString('en-GB')} XP`;
    case 'net_positive':
      return `${value > 0 ? '+' : ''}${Math.round(value)}`;
    case 'hot_streak':
      return plural(Math.round(value), 'win');
    case 'appearance_king':
      return plural(Math.round(value), 'cap');
    case 'win_rate_leader':
    case 'blue_blood':
    case 'dutch_master':
      return `${value.toFixed(1)}%`;
    default:
      return plural(Math.round(value), 'game');
  }
}

/** How far behind someone is, in the award's own unit ("12 XP", "2 games", "1.4%"). Null for score-based awards. */
export function trophyGap(category: AwardCategory, gap: number): string | null {
  if (TROPHY_META[category].scored) return null;
  const g = Math.max(0, gap);
  switch (category) {
    case 'xp_champion':
      return `${Math.round(g).toLocaleString('en-GB')} XP`;
    case 'net_positive':
      return plural(Math.round(g), 'goal');
    case 'hot_streak':
      return plural(Math.round(g), 'win');
    case 'appearance_king':
      return plural(Math.round(g), 'cap');
    case 'win_rate_leader':
    case 'blue_blood':
    case 'dutch_master':
      return `${trim1(Number(g.toFixed(1)))}%`;
    default:
      return plural(Math.round(g), 'game');
  }
}

/** 1st, 2nd, 3rd, 11th, 22nd… */
export function ordinal(n: number): string {
  const s = n % 100;
  if (s >= 11 && s <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
}
